import { useState, useMemo, useCallback } from 'react';
import {
  groupCategoriesByLevel2,
  filterCategoriesBySearch,
} from 'react/components/apps/audience/AudienceExplorer/AudienceInsightsWidget/QuickTable/utils/categoryGrouping';

/**
 * Hook for managing categories table data with grouping, sorting, and search
 * @param {Object} options - Configuration options
 * @param {Array} options.data - Raw categories data array
 * @param {Object} options.searchInputRef - Ref for the search input element
 * @returns {Object} Grouped data, sorted data, search handlers, and sort state
 */
const useCategoriesData = ({ data, searchInputRef }) => {
  const [search, setSearch] = useState('');
  const [searchOpened, setSearchOpened] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    order: 'uniqueness-index',
    reverse: true,
  });
  const [groupByPrimaryCategory, setGroupByPrimaryCategory] = useState(false);

  const sortCollection = useCallback((collection, order, reverse) => {
    const sorted = [...(collection || [])].sort((a, b) => {
      const aVal = a?.[order];
      const bVal = b?.[order];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return reverse ? bVal - aVal : aVal - bVal;
      }

      const aStr = (aVal ?? '').toString().toLowerCase();
      const bStr = (bVal ?? '').toString().toLowerCase();
      if (aStr < bStr) return reverse ? 1 : -1;
      if (aStr > bStr) return reverse ? -1 : 1;
      return 0;
    });
    return sorted;
  }, []);

  // Filter data by search term
  // When not searching, limit to top 200 by audience skew (uniqueness-index)
  // When searching, show ALL matching results
  const filteredData = useMemo(() => {
    const filtered = filterCategoriesBySearch(data || [], search);
    // Only limit to 200 when NOT searching - allow full search results
    if (!search && filtered.length > 200) {
      return filtered.slice(0, 200);
    }
    return filtered;
  }, [data, search]);

  // Group filtered data by Level 2 (path[0])
  const groupedData = useMemo(
    () => groupCategoriesByLevel2(filteredData, sortConfig.order, sortConfig.reverse),
    [filteredData, sortConfig]
  );
  const sortedData = useMemo(
    () => sortCollection(filteredData, sortConfig.order, sortConfig.reverse),
    [filteredData, sortConfig, sortCollection]
  );

  const handleSort = useCallback((columnId) => {
    setSortConfig((prev) => {
      const isSameColumn = prev.order === columnId;
      return {
        order: columnId,
        reverse: isSameColumn ? !prev.reverse : true,
      };
    });
  }, []);

  // Toggle search open/close
  const handleToggleSearch = useCallback(() => {
    setSearchOpened((prev) => {
      if (!prev && searchInputRef?.current) {
        setTimeout(() => {
          searchInputRef.current && searchInputRef.current.focus();
        }, 0);
      }
      return !prev;
    });
  }, [searchInputRef]);

  // Handle search change
  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value || '');
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearch('');
    setSearchOpened(false);
  }, []);

  return {
    search,
    searchOpened,
    setSearch,
    handleToggleSearch,
    handleSearchChange,
    clearSearch,
    groupedData,
    sortedData,
    groupByPrimaryCategory,
    setGroupByPrimaryCategory,
    sortConfig,
    handleSort,
    totalCategories: filteredData.length,
  };
};

export default useCategoriesData;
